import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { TextAction } from '@node-space/storybook-components'
import { Box, Button, Text } from '@node-space/ui-components'
import { WindowTarget } from 'constants/General'
import { useEmbeddedCustomerAgreementsMutation } from 'hooks/mutations/useEmbeddedCustomerAgreementsMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { getAgreementLinks } from 'pages/Verification/Review/helpers/utils'
import { getListToSentencePrefix } from 'utils/utils'
import { EmbeddedCustomerAgreement, UIConfig } from '../helpers/types'

export interface AgreementsFormProps {
  reference: string
  redirectUri?: string
  isUsEntity?: boolean
  agreements: EmbeddedCustomerAgreement[]
  uiConfig?: UIConfig
  onSuccess: () => void
}

export const AgreementsForm = ({
  reference,
  redirectUri,
  isUsEntity,
  agreements,
  onSuccess,
  uiConfig,
}: AgreementsFormProps) => {
  const { t } = useTranslation()
  const { requestError, resetRequestError, handleRequestError } = useMappedErrorMessage()

  const agreementsLinks = getAgreementLinks(agreements, t)

  const { mutate: putEmbeddedCustomerAgreements, isPending: isSubmittingAgreements } =
    useEmbeddedCustomerAgreementsMutation()

  const goToRedirect = () => {
    window?.open(redirectUri, WindowTarget.SAME_TAB)
  }

  const onSubmitAgreements = () => {
    resetRequestError()
    putEmbeddedCustomerAgreements(
      { reference },
      {
        onError: error => handleRequestError(error, 'putEmbeddedCustomerAgreements'),
        onSuccess: () => {
          onSuccess()
          redirectUri && goToRedirect()
        },
      }
    )
  }

  const textClassName = clsx(`md:text-left sm:text-sm text-base`, {
    'text-white': uiConfig?.isDarkMode,
  })

  return (
    <>
      {requestError?.show && (
        <FormSubmitError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}
      <Text isCentered className={textClassName}>
        {isUsEntity
          ? t('embeddedCustomerTerms.introductionUs')
          : t('embeddedCustomerTerms.introduction')}
        {agreementsLinks?.map(({ label, url }, index) => (
          <span key={label}>
            {getListToSentencePrefix(agreementsLinks?.length, index, t)}
            <span
              {...(uiConfig?.primaryColor && { style: { color: `#${uiConfig?.primaryColor}` } })}
            >
              <TextAction
                linkTo={url}
                text={label}
                {...(uiConfig?.primaryColor && { inheritColor: true })}
              />
            </span>
          </span>
        ))}
        .
      </Text>
      <Text isCentered className={textClassName}>
        {t('embeddedCustomerTerms.bySubmitting')}
      </Text>
      <Box
        flexCol
        justify={redirectUri ? 'between' : 'center'}
        gapX={8}
        gapY={24}
        paddingT={8}
        className="sm:flex-row-reverse md:pt-4"
      >
        <Button
          loading={isSubmittingAgreements}
          onClick={onSubmitAgreements}
          full
          className="sm:max-w-[318px]"
          style={{ backgroundColor: `#${uiConfig?.primaryColor}` }}
        >
          {t('submit')}
        </Button>
        {redirectUri && (
          <Button
            variant={uiConfig?.primaryColor ? 'ghost' : 'secondary'}
            disabled={isSubmittingAgreements}
            onClick={goToRedirect}
            full
            className="sm:max-w-[318px]"
            style={{
              color: uiConfig?.primaryColor && `#${uiConfig?.primaryColor}`,
              backgroundColor: uiConfig?.primaryColor && '#F5F5F4',
            }}
          >
            {t('cancel')}
          </Button>
        )}
      </Box>
    </>
  )
}
